import type { ComponentPropsWithoutRef, RefAttributes } from "react";
import { DateInput, DateInputProps } from "@mantine/dates";
import { InputMask } from "@react-input/mask";
import dayjs from "dayjs";

type MaskedDateInputProps = Omit<DateInputProps, "component">;

// Custom input component that wraps InputMask from @react-input/mask
// In React 19, ref can be passed as a regular prop
function MaskedInput({
  ref,
  value,
  onChange,
  onFocus,
  onBlur,
  ...restProps
}: ComponentPropsWithoutRef<"input"> & RefAttributes<HTMLInputElement>) {
  return (
    <InputMask
      {...restProps}
      ref={ref}
      mask="__.__.____"
      replacement={{ _: /\d/ }}
      showMask
      separate={true}
      value={value}
      onChange={(e) => {
        const maskedValue = e.target.value;
        // Check if value is empty or only contains placeholders/separators
        const digitsOnly = maskedValue.replace(/\D/g, "");

        // If only placeholders remain, treat as empty and set to empty string
        // This will cause DateInput to set value to null
        if (digitsOnly.length === 0) {
          const emptyEvent = {
            ...e,
            currentTarget: { ...e.currentTarget, value: "" },
            target: { ...e.target, value: "" },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange?.(emptyEvent);
        } else {
          onChange?.(e);
        }
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      type="text"
    />
  );
}

export function MaskedDateInput(props: MaskedDateInputProps) {
  return (
    <DateInput
      {...props}
      component={MaskedInput}
      valueFormat="DD.MM.YYYY"
      dateParser={(val: string) => {
        // If value is empty, return null to clear the date
        if (!val || val.trim() === "") {
          return null;
        }
        // Try parsing with DD.MM.YYYY format first (from mask)
        const parsed = dayjs(val, "DD.MM.YYYY", true);
        if (parsed.isValid()) {
          return parsed.toDate();
        }
        // Fall back to default parser if provided, or try other formats
        if (props.dateParser) {
          return props.dateParser(val);
        }
        // Try common formats
        return dayjs(val, ["L", "DD MM", "DD.MM.YYYY"]).toDate();
      }}
    />
  );
}
